import { ref } from "vue";
import ProjectService from "../services/projectService.js";

const PAGE_STEP = 30;

// "Docs" only ever shows standalone documents (singleton projects) — real,
// multi-document projects are out of scope here (see Projects/ProjectsPanel).
// Module-level singleton so Docs.vue and DocsPanel.vue share one fetched list.
const docs = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMore = ref(true);
let requestedCount = PAGE_STEP;
let hasLoadedOnce = false;
let lastSortBy = "DATE_ADDED";
let lastSortDirection = "DESC";
let fetchPromise = null;

const projectService = new ProjectService();

export function useDocs() {
  // Concurrent callers (e.g. several singleton uploads finishing around the
  // same time, each triggering their own refresh()) coalesce onto one
  // in-flight fetch instead of racing independent ones — otherwise whichever
  // response resolves last wins even if it started first with staler data.
  function fetchPage(sortBy, sortDirection) {
    if (fetchPromise) return fetchPromise;
    lastSortBy = sortBy;
    lastSortDirection = sortDirection;
    fetchPromise = projectService.getSingletonProjects({ first: requestedCount, sortBy, sortDirection })
      .then((res) => {
        docs.value = res.documents ?? [];
        hasMore.value = res.hasNextPage;
      })
      .finally(() => { fetchPromise = null; });
    return fetchPromise;
  }

  async function load(sortBy = "DATE_ADDED", sortDirection = "DESC") {
    if (hasLoadedOnce) return;
    hasLoadedOnce = true;
    isLoading.value = true;
    try {
      await fetchPage(sortBy, sortDirection);
    } finally {
      isLoading.value = false;
    }
  }

  // No cursor is exposed by this API, so "load more" just asks for a bigger
  // first page rather than paginating from an offset.
  async function loadMore(sortBy = lastSortBy, sortDirection = lastSortDirection) {
    if (!hasMore.value || isLoadingMore.value) return;
    isLoadingMore.value = true;
    requestedCount += PAGE_STEP;
    try {
      await fetchPage(sortBy, sortDirection);
    } finally {
      isLoadingMore.value = false;
    }
  }

  // Sort is applied server-side, so changing it re-fetches from scratch.
  async function resort(sortBy, sortDirection) {
    requestedCount = PAGE_STEP;
    isLoading.value = true;
    try {
      await fetchPage(sortBy, sortDirection);
    } finally {
      isLoading.value = false;
    }
  }

  // Silently re-fetch in place (no loading flash) — used after uploading a
  // new standalone document, so it shows up without the user refreshing.
  async function refresh() {
    if (!hasLoadedOnce) return;
    await fetchPage(lastSortBy, lastSortDirection);
  }

  return { docs, isLoading, isLoadingMore, hasMore, load, loadMore, resort, refresh };
}
